import { useState } from 'react'
import { CheckCircle, ShieldCheck, Prohibit } from '@phosphor-icons/react'
import { useI18n } from '@/i18n'
import { newId } from '@/lib/ids'
import { Badge, Banner, Button, ChoiceGroup, Field, IntegerInput, PhotoCapture } from '@/ui'
import type { TagEntry } from '../../commands'
import { ActionBar } from '../../components/GateScreen'
import { GuardedScanButton } from '../../components/Scan'
import { guardFor } from '../../data/useGate'
import { draftPayloads, nextStep } from '../../model/delivery'
import { useWizard } from './context'
import { VoidInDraftSheet } from './StepTag'

/** F4.7 — नमूना सील करें · Seal the sample: seal from her strip, weight to the crop minimum, photo. */
export function StepSample() {
  const { t } = useI18n()
  const { d, dispatch, roster, session, used, crop, stamp, requestSupervisor } = useWizard()
  const minG = crop?.sample_min_g ?? 500
  const s = d.sample
  const [seal, setSeal] = useState<{ payload: string; entry: TagEntry } | null>(null)
  const [weight, setWeight] = useState<number | null>(null)
  const [photo, setPhoto] = useState<string | null>(null)
  const [bigOk, setBigOk] = useState(false)
  const [voiding, setVoiding] = useState(false)
  const guard = guardFor(session, roster, used, 'SEAL', draftPayloads(d))
  const under = weight !== null && weight < minG
  const big = weight !== null && weight > roster.config.sample_confirm_above_g

  const save = (allowedBy: string | null) => {
    if (!seal || weight === null || !photo) return
    dispatch({
      type: 'set_sample',
      sample: {
        id: newId(),
        seal_payload: seal.payload,
        seal_entry: seal.entry,
        weight_g: weight,
        collected_at: stamp(),
        photo_media_id: photo,
        underweight_allowed_by: allowedBy,
      },
    })
  }

  if (s) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3 rounded-card border border-ok/30 bg-ok-wash px-3 py-3">
          <CheckCircle size={28} weight="fill" className="text-ok" aria-hidden="true" />
          <div className="flex-1">
            <div className="font-mono text-[16px]">{s.seal_payload}</div>
            {s.weight_g !== null && (
              <div className="text-[14px]">{t('gate.sample.grams', { g: s.weight_g })}</div>
            )}
          </div>
          {s.underweight_allowed_by && <Badge tone="warn">{t('gate.sample.allowed')}</Badge>}
        </div>
        <Button
          size="touch"
          variant="secondary"
          leadingIcon={<Prohibit aria-hidden="true" />}
          onClick={() => setVoiding(true)}
        >
          {t('gate.sample.voidSeal')}
        </Button>
        <ActionBar>
          <Button
            size="xl"
            fullWidth
            onClick={() => dispatch({ type: 'goto', step: nextStep(d, 'sample') })}
          >
            {t('common.next')}
          </Button>
        </ActionBar>
        {voiding && <VoidInDraftSheet kind="SEAL" onClose={() => setVoiding(false)} />}
      </div>
    )
  }

  const ready = !!seal && weight !== null && weight > 0 && !!photo && (!big || bigOk)
  return (
    <div className="flex flex-col gap-4">
      <Field label={t('gate.sample.seal')} required>
        {seal ? (
          <div className="flex items-center gap-2 rounded-chip border border-ok/30 bg-ok-wash px-3 py-2">
            <span className="flex-1 font-mono text-[16px]">{seal.payload}</span>
            <Button size="sm" variant="ghost" onClick={() => setSeal(null)}>
              {t('gate.change')}
            </Button>
          </div>
        ) : (
          <GuardedScanButton
            guard={guard}
            label={t('gate.sample.scanSeal')}
            onAccept={(payload, entry) => setSeal({ payload, entry })}
            onRefuse={(scan) => dispatch({ type: 'refuse_scan', scan })}
          />
        )}
      </Field>
      <Field
        label={t('gate.sample.weight')}
        required
        hint={t('gate.sample.min', { g: minG })}
        error={under ? t('gate.sample.under', { g: minG }) : undefined}
      >
        <IntegerInput
          size="touch"
          maxDigits={5}
          value={weight}
          onChange={setWeight}
          trailing={<span className="font-mono">g</span>}
        />
      </Field>
      {big && (
        <ChoiceGroup
          label={t('gate.sample.confirmBig', { g: weight ?? 0 })}
          options={[{ value: 'yes', label: t('gate.sample.confirmBig', { g: weight ?? 0 }) }]}
          value={bigOk ? 'yes' : null}
          onChange={() => setBigOk(true)}
        />
      )}
      <Field label={t('gate.sample.photo')} required hint={t('gate.sample.photoHint')}>
        <PhotoCapture value={photo} onChange={setPhoto} />
      </Field>
      {under && <Banner tone="warn">{t('gate.sample.moreDraws')}</Banner>}
      <ActionBar>
        <Button size="xl" fullWidth disabled={!ready || under} onClick={() => save(null)}>
          {t('common.save')}
        </Button>
        {under && (
          <Button
            size="touch"
            variant="ghost"
            fullWidth
            disabled={!ready}
            leadingIcon={<ShieldCheck aria-hidden="true" />}
            onClick={async () => {
              const sup = await requestSupervisor(
                t('gate.sample.supReason', { g: weight ?? 0, min: minG }),
              )
              if (sup) save(sup.user_id)
            }}
          >
            {t('gate.sample.supAllows')}
          </Button>
        )}
      </ActionBar>
    </div>
  )
}
